import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Package,
  Tag,
  Ruler,
  IndianRupee,
  Hash,
  Truck,
  ShieldAlert,
  Languages,
  PartyPopper,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatNPR, formatQty, formatSize } from "../lib/utils";
import { deriveFloor, markupFor } from "../lib/pricing";
import { encodeAmount } from "../lib/cipher";
import {
  suggestFamilies,
  sizeChipsFor,
  groupForName,
} from "../lib/families";
import { suggestBrands } from "../lib/brands";
import { queueIfOffline, getQueue } from "../lib/outbox";
import { fetchWithSnapshot, applyPendingWrites, KEYS } from "../lib/snapshot";
import { toast } from "sonner";
import romanizeToNepali from "../lib/romanization";

const API_BASE = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") || "";
const API = `${API_BASE}/api`;

const num = (v) => (v === "" || v === null || v === undefined ? null : parseFloat(v));

/**
 * Adding a product as a conversation: one question per screen, next, next, done.
 *
 * The shopkeeper asked for this outright, over the single long form -- their
 * point being that a page of eighteen fields gets skimmed, while questions asked
 * one at a time actually get answered. The cost of a wizard is taps, so every
 * step earns its place by skipping itself when the answer is already known:
 * picking "Karai" from the list answers the category, location, units and size
 * unit in one go, and those screens simply do not appear.
 *
 * Editing stays on the one-page form. An edit exists to reach one known field,
 * and walking a wizard to fix a price would be the wrong tool.
 */
export default function AddProductWizard() {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [units, setUnits] = useState([]);
  const [sizeUnits, setSizeUnits] = useState([]);
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);

  const [family, setFamily] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [addedCount, setAddedCount] = useState(0);
  const [mrpTouched, setMrpTouched] = useState(false);

  // How the cost is known. A dealer bill for loose brass is written per kilo
  // -- "5 kg @ Rs 1000/kg" -- never per piece. The shopkeeper counted the
  // pieces, so rate times kilos over pieces is arithmetic the app should do,
  // not them.
  const [costMode, setCostMode] = useState("piece");
  const [kgRate, setKgRate] = useState("");
  const [totalKg, setTotalKg] = useState("");

  const [form, setForm] = useState({
    name_en: "",
    name_np: "",
    brand: "",
    category: "",
    location: "",
    size_value: "",
    size_unit: "",
    gauge: "",
    unit: "pcs",
    purchase_unit: "",
    pack_size: "",
    variant_group: "",
    min_price: "",
    cost_price: "",
    mrp: "",
    quantity: "",
    quantity_type: "exact",
    low_stock_threshold: "5",
    supplier_id: "",
  });

  const inputRef = useRef(null);

  const sizeUnitsById = useMemo(
    () => Object.fromEntries(sizeUnits.map((u) => [u.id, u])),
    [sizeUnits],
  );

  useEffect(() => {
    const load = async (key, path, fallback = []) => {
      try {
        const { data } = await fetchWithSnapshot(key, async () => {
          const res = await axios.get(`${API}${path}`, getAuthHeader());
          return res.data;
        });
        return data;
      } catch (err) {
        console.error(`Failed to load ${path}:`, err);
        return fallback;
      }
    };

    (async () => {
      const [sups, cats, locs, us, sus, sh, prods] = await Promise.all([
        load(KEYS.suppliers, "/suppliers"),
        load(KEYS.categories, "/categories"),
        load(KEYS.locations, "/locations"),
        load(KEYS.units, "/units"),
        load(KEYS.sizeUnits, "/size-units"),
        load(KEYS.shop, "/shop", null),
        load(KEYS.products, "/products"),
      ]);
      setSuppliers(sups);
      setCategories(cats);
      setLocations(locs);
      setUnits(us);
      setSizeUnits(sus);
      setShop(sh);
      const withPending = applyPendingWrites(prods, getQueue());
      setProducts(withPending);

      // Open on what the shop stocks most of, not what sorts first -- the same
      // rule the one-page form learnt the hard way when "Boxed Items" won on
      // alphabetical order.
      const commonest = (field, rows) => {
        const counts = {};
        for (const p of withPending) {
          if (p[field]) counts[p[field]] = (counts[p[field]] || 0) + 1;
        }
        const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
        return rows.find((r) => r.id === best?.[0]) || rows[0];
      };
      if (cats.length && locs.length) {
        const cat = commonest("category", cats);
        setForm((prev) => ({
          ...prev,
          category: cat.id,
          location: commonest("location", locs).id,
          size_unit: cat.default_size_unit || "",
        }));
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- derived ---------------------------------------------------------------

  const activeCategory = useMemo(
    () => categories.find((c) => c.id === form.category) || null,
    [categories, form.category],
  );
  const activeUnit = useMemo(
    () => units.find((u) => u.id === form.unit) || null,
    [units, form.unit],
  );
  const markup = markupFor(activeCategory, shop);
  const minMargin = Number(shop?.min_margin_percent) || 10;

  const suggestions = useMemo(
    () => (family ? [] : suggestFamilies(form.name_en)),
    [family, form.name_en],
  );

  const group = useMemo(
    () => groupForName(family?.en || form.name_en, form.category, products),
    [family, form.name_en, form.category, products],
  );

  const chips = useMemo(
    () => sizeChipsFor(family, products, group),
    [family, products, group],
  );

  const existingGroups = useMemo(() => {
    const seen = new Map();
    for (const p of products) {
      if (!p.variant_group || seen.has(p.variant_group)) continue;
      seen.set(p.variant_group, p.name_en);
    }
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [products]);

  const brandOptions = useMemo(
    () => suggestBrands(form.brand, form.category, products),
    [form.brand, form.category, products],
  );

  const duplicate = useMemo(() => {
    const name = (family?.en || form.name_en).trim().toLowerCase();
    if (!name) return null;
    const size = num(form.size_value);
    const gauge = num(form.gauge);
    return (
      products.find(
        (p) =>
          p.name_en?.trim().toLowerCase() === name &&
          (p.size_value ?? null) === size &&
          (p.gauge ?? null) === gauge,
      ) || null
    );
  }, [products, family, form.name_en, form.size_value, form.gauge]);

  const qtyN = num(form.quantity) || 0;
  const kgN = num(totalKg) || 0;
  const rateN = num(kgRate) || 0;
  const lotTotal = rateN * kgN;
  const cost =
    costMode === "kg"
      ? qtyN > 0 && lotTotal > 0
        ? lotTotal / qtyN
        : 0
      : num(form.cost_price) || 0;
  const mrp = num(form.mrp) || 0;

  // In total mode the per-piece cost moves whenever the bill figure or the
  // count does, so the suggested tag price has to follow it the same way
  // typing a per-piece cost would.
  useEffect(() => {
    if (costMode !== "kg" || mrpTouched || !(cost > 0)) return;
    const next = String(Math.round(cost * markup));
    setForm((prev) => (prev.mrp === next ? prev : { ...prev, mrp: next }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costMode, kgRate, totalKg, form.quantity, markup, mrpTouched]);
  const floor = deriveFloor(cost, minMargin, form.min_price === "" ? null : num(form.min_price));
  const floorCode = encodeAmount(floor, shop?.cipher_keyword);

  // ---- the steps -------------------------------------------------------------

  // Built per item rather than fixed: a step whose answer is already known does
  // not appear, which is what keeps next-next-next from costing a thousand
  // items' worth of extra taps.
  const steps = useMemo(() => {
    const out = ["item"];
    // Picking from the list answers category and location; a typed name needs
    // asking.
    if (!family) out.push("where");
    // Unsized things (jharu, chamcha) skip the size question entirely.
    if (family ? Boolean(family.sizeUnit) : true) out.push("size");
    out.push("brand", "numbers", "extras", "done");
    return out;
  }, [family]);

  const step = steps[Math.min(stepIndex, steps.length - 1)];

  // Focus follows the question, and Enter answers it.
  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 120);
    return () => clearTimeout(t);
  }, [step]);

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const pickFamily = (f) => {
    setFamily(f);
    setForm((prev) => ({
      ...prev,
      name_en: f.en,
      name_np: f.np,
      category: f.cat,
      location: f.loc || prev.location,
      size_unit: f.sizeUnit || "",
      unit: f.unit || "pcs",
      purchase_unit: f.purchaseUnit || prev.purchase_unit,
      size_value: "",
      gauge: "",
    }));
  };

  const handleNameChange = (value) => {
    const fromFamily = Boolean(family);
    setFamily(null);
    setForm((prev) => ({
      ...prev,
      name_en: value,
      name_np: fromFamily ? "" : prev.name_np,
    }));
  };

  const handleCostChange = (value) => {
    const c = num(value);
    setForm((prev) => ({
      ...prev,
      cost_price: value,
      mrp: !mrpTouched && c > 0 ? String(Math.round(c * markup)) : prev.mrp,
    }));
  };

  // What blocks Next on the current step. Everything else is allowed through --
  // a wizard that nags on optional questions teaches people to fear Next.
  const blocker = () => {
    if (step === "item" && !form.name_en.trim()) {
      return "Type or pick an item / सामानको नाम";
    }
    if (step === "numbers" && (!form.mrp || mrp <= 0)) {
      return "A tag price is needed / मूल्य चाहिन्छ";
    }
    return null;
  };

  const next = () => {
    const why = blocker();
    if (why) {
      toast.error(why);
      return;
    }
    setStepIndex((i) => Math.min(i + 1, steps.length - 1));
  };

  const back = () => {
    if (stepIndex === 0) navigate("/inventory");
    else setStepIndex((i) => i - 1);
  };

  // ---- saving ----------------------------------------------------------------

  const save = async (another) => {
    setSaving(true);
    const name = (family?.en || form.name_en).trim();
    const payload = {
      name_en: name,
      name_np: form.name_np.trim() || romanizeToNepali(name) || "",
      brand: form.brand.trim(),
      category: form.category,
      location: form.location,
      size_value: num(form.size_value),
      size_unit: form.size_value === "" ? null : form.size_unit || null,
      gauge: num(form.gauge),
      // A typed override wins; otherwise the derived group, as everywhere else.
      variant_group: form.variant_group.trim() || group,
      unit: form.unit || "pcs",
      // Priced by the kilo means bought by the kilo, whatever was picked.
      purchase_unit: costMode === "kg" && kgN > 0 ? "kg" : form.purchase_unit || null,
      pack_size: num(form.pack_size),
      min_price: num(form.min_price),
      cost_price: Math.round(cost * 100) / 100,
      // A weighed lot that was also counted gives the piece weight for free --
      // the same derivation the purchase endpoint does -- and that is what
      // later lets this item sell by weight off the scale.
      weight_g: costMode === "kg" && kgN > 0 && qtyN > 0 ? (kgN * 1000) / qtyN : null,
      mrp,
      quantity: num(form.quantity) || 0,
      quantity_type: form.quantity_type,
      low_stock_threshold: num(form.low_stock_threshold) ?? 5,
      supplier_id: form.supplier_id || null,
    };
    // The id is decided here so an offline replay is a no-op, not a duplicate.
    const body = { id: crypto.randomUUID(), ...payload };

    const nextOne = (saved) => {
      setProducts((prev) => [...prev, saved]);
      setAddedCount((n) => n + 1);
      setFamily(null);
      setMrpTouched(false);
      setCostMode("piece");
      setKgRate("");
      setTotalKg("");
      // Back to the first question, with the shelf kept: category, location and
      // brand carry over, everything about the item itself clears.
      setForm((prev) => ({
        ...prev,
        name_en: "",
        name_np: "",
        size_value: "",
        gauge: "",
        variant_group: "",
        min_price: "",
        pack_size: "",
        quantity: "",
        cost_price: "",
        mrp: "",
      }));
      setStepIndex(0);
    };

    try {
      const res = await axios.post(`${API}/products`, body, getAuthHeader());
      toast.success(`${res.data.sku} · Saved! / सुरक्षित भयो!`);
      if (another) nextOne(res.data);
      else navigate("/inventory");
    } catch (err) {
      if (queueIfOffline(err, { method: "post", path: "/products", body, label: name })) {
        if (another) nextOne({ ...body, _pending: true });
        else navigate("/inventory");
        return;
      }
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  // ---- rendering -------------------------------------------------------------

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F5] flex items-center justify-center">
        <div className="loader" />
      </div>
    );
  }

  const field = "w-full h-14 px-4 text-lg border border-gray-300 rounded-xl";
  const bigLabel = "text-xl font-semibold text-[#2D2D2D]";

  const STEP_META = {
    item: { icon: Package, en: "What is it?", np: "के थप्ने?" },
    where: { icon: Tag, en: "Where does it go?", np: "कहाँ राख्ने?" },
    size: { icon: Ruler, en: "What size?", np: "कुन नाप?" },
    brand: { icon: Tag, en: "Which brand?", np: "कुन ब्रान्ड?" },
    numbers: { icon: IndianRupee, en: "How many, at what price?", np: "कति, कतिमा?" },
    extras: { icon: Hash, en: "Anything else?", np: "अरू केही?" },
    done: { icon: Check, en: "All good?", np: "ठिक छ?" },
  };
  const Icon = STEP_META[step].icon;

  return (
    <div className="min-h-screen bg-[#F9F9F5] flex flex-col" data-testid="add-wizard">
      {/* Header: back, progress dots, running count */}
      <header className="sticky top-0 z-30 bg-white border-b border-gray-200">
        <div className="px-4 py-3 flex items-center gap-3">
          <button
            onClick={back}
            className="p-2 hover:bg-gray-100 rounded-full"
            data-testid="wizard-back"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>

          <div className="flex-1 flex items-center justify-center gap-1.5">
            {steps.map((s, i) => (
              <span
                key={s}
                className={`rounded-full transition-all ${
                  i === stepIndex
                    ? "w-6 h-2 bg-[#8B0000]"
                    : i < stepIndex
                      ? "w-2 h-2 bg-[#8B0000]/50"
                      : "w-2 h-2 bg-gray-300"
                }`}
              />
            ))}
          </div>

          <span
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              addedCount > 0 ? "bg-green-50 text-green-700" : "text-transparent"
            }`}
            data-testid="wizard-count"
          >
            {addedCount > 0 ? `${addedCount} ✓` : "0"}
          </span>
        </div>
      </header>

      {/* The one question */}
      <form
        className="flex-1 p-5 pb-40 max-w-lg w-full mx-auto space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (step === "done") save(true);
          else next();
        }}
      >
        <div className="flex items-center gap-3 pt-2">
          <div className="w-10 h-10 rounded-xl bg-[#8B0000] flex items-center justify-center shrink-0">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className={bigLabel}>{STEP_META[step].en}</h1>
            <p className="text-gray-500 font-nepali">{STEP_META[step].np}</p>
          </div>
        </div>

        {/* ---- item ---- */}
        {step === "item" && (
          <>
            <input
              ref={inputRef}
              type="text"
              value={form.name_en}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="kar…  /  cook…"
              className={field}
              data-testid="wiz-name"
            />

            {suggestions.length > 0 && (
              <div className="space-y-2">
                {suggestions.map((f) => (
                  <button
                    key={f.en}
                    type="button"
                    onClick={() => {
                      pickFamily(f);
                      // Picking IS answering -- straight to the next question.
                      setStepIndex((i) => i + 1);
                    }}
                    className="w-full p-4 bg-white rounded-xl border-2 border-gray-200 text-left
                               hover:border-[#8B0000] active:scale-[0.98] transition-all
                               flex items-center justify-between"
                    data-testid={`wiz-suggest-${f.en}`}
                  >
                    <span className="font-medium">
                      {f.en}
                      <span className="ml-2 text-gray-500 font-nepali">{f.np}</span>
                    </span>
                    <ArrowRight className="w-4 h-4 text-gray-400" />
                  </button>
                ))}
              </div>
            )}

            {family && (
              <div className="p-3 rounded-xl bg-green-50 text-green-700 text-sm">
                {family.en} — category, location and units filled in. Next!
              </div>
            )}

            {!family && form.name_en.trim() && !form.name_np && (
              <button
                type="button"
                onClick={() => {
                  const np = romanizeToNepali(form.name_en.trim());
                  if (np) set("name_np", np);
                }}
                className="inline-flex items-center gap-1.5 text-sm text-[#8B0000] font-medium"
              >
                <Languages className="w-4 h-4" />
                Write it in Nepali
              </button>
            )}
            {form.name_np && (
              <p className="text-gray-500 font-nepali">{form.name_np}</p>
            )}
          </>
        )}

        {/* ---- where (typed items only) ---- */}
        {step === "where" && (
          <>
            <div>
              <label className="text-sm text-gray-500 mb-1 block">Category / वर्ग</label>
              <select
                value={form.category}
                onChange={(e) => {
                  const next = categories.find((c) => c.id === e.target.value);
                  setForm((prev) => ({
                    ...prev,
                    category: e.target.value,
                    size_unit: next?.default_size_unit || prev.size_unit,
                  }));
                }}
                className={`${field} bg-white`}
                data-testid="wiz-category"
              >
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name_en} / {c.name_np}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-500 mb-1 block">Location / स्थान</label>
              <select
                value={form.location}
                onChange={(e) => set("location", e.target.value)}
                className={`${field} bg-white`}
                data-testid="wiz-location"
              >
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name_en} / {l.name_np}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        {/* ---- size ---- */}
        {step === "size" && (
          <>
            {chips.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {chips.map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => {
                      set("size_value", String(v));
                      // Tapping a chip answers the question.
                      setStepIndex((i) => i + 1);
                    }}
                    className={`px-5 h-14 text-lg rounded-xl font-semibold transition-colors ${
                      num(form.size_value) === v
                        ? "bg-[#8B0000] text-white"
                        : "bg-white border-2 border-gray-200 text-gray-700"
                    }`}
                    data-testid={`wiz-chip-${v}`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                inputMode="decimal"
                value={form.size_value}
                onChange={(e) => set("size_value", e.target.value)}
                placeholder={chips.length ? "or another size" : "size"}
                className={`${field} flex-1`}
                data-testid="wiz-size"
              />
              <select
                value={form.size_unit}
                onChange={(e) => set("size_unit", e.target.value)}
                className="h-14 px-3 border border-gray-300 rounded-xl bg-white"
                data-testid="wiz-size-unit"
              >
                <option value="">—</option>
                {sizeUnits.map((su) => (
                  <option key={su.id} value={su.id}>
                    {su.name_np} ({su.name_en})
                  </option>
                ))}
              </select>
            </div>

            {/* Every item, not only list-picked thali -- a typed-in gauged item
                had nowhere to say so. Optional, so it costs nothing to pass. */}
            <div>
              <label className="text-sm text-gray-500 mb-1 block">
                Gauge / गेज — optional, lower is thicker and dearer
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={form.gauge}
                onChange={(e) => set("gauge", e.target.value)}
                placeholder="22"
                className={field}
                data-testid="wiz-gauge"
              />
            </div>

            {duplicate && (
              <div
                className="p-3 rounded-xl bg-amber-50 text-amber-800 text-sm"
                data-testid="wiz-duplicate"
              >
                Already on the shelf: {duplicate.name_en}{" "}
                <span className="font-nepali">
                  {formatSize(duplicate, sizeUnitsById)}
                </span>{" "}
                — {formatQty(duplicate.quantity)} in stock. Another size, or open
                that one instead.
              </div>
            )}

            {!form.size_value && (
              <p className="text-sm text-gray-400">
                No size? Just press Next / नाप छैन भने Next
              </p>
            )}
          </>
        )}

        {/* ---- brand ---- */}
        {step === "brand" && (
          <>
            {brandOptions.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {brandOptions.map((b) => (
                  <button
                    key={b}
                    type="button"
                    onClick={() => {
                      set("brand", b);
                      setStepIndex((i) => i + 1);
                    }}
                    className={`px-4 h-12 rounded-xl font-medium transition-colors ${
                      form.brand === b
                        ? "bg-[#8B0000] text-white"
                        : "bg-white border-2 border-gray-200 text-gray-700"
                    }`}
                    data-testid={`wiz-brand-${b}`}
                  >
                    {b}
                  </button>
                ))}
              </div>
            )}

            <input
              ref={inputRef}
              type="text"
              value={form.brand}
              onChange={(e) => set("brand", e.target.value)}
              placeholder={brandOptions.length ? "or type one" : "type a brand"}
              className={field}
              data-testid="wiz-brand"
            />

            <button
              type="button"
              onClick={() => {
                set("brand", "");
                setStepIndex((i) => i + 1);
              }}
              className="w-full h-12 rounded-xl bg-gray-100 text-gray-600 font-medium"
              data-testid="wiz-no-brand"
            >
              No brand / ब्रान्ड छैन — most steel and brass
            </button>
          </>
        )}

        {/* ---- numbers: quantity, cost, tag price ---- */}
        {step === "numbers" && (
          <>
            <div>
              <label className="text-sm text-gray-500 mb-1 block">
                How many{activeUnit ? ` (${activeUnit.name_np})` : ""} / कति वटा
              </label>
              <input
                ref={inputRef}
                type="text"
                inputMode={activeUnit?.allow_decimal ? "decimal" : "numeric"}
                value={form.quantity}
                onChange={(e) => set("quantity", e.target.value)}
                placeholder="0"
                className={field}
                data-testid="wiz-qty"
              />
            </div>

            {/* A dealer bill for loose goods is written per kilo, never per
                piece. Taken as written, and the division shown as it happens. */}
            <div>
              <label className="text-sm text-gray-500 mb-1 block">
                Price you know / मूल्य कसरी थाहा छ
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setCostMode("piece")}
                  className={`flex-1 h-11 rounded-xl font-medium text-sm ${
                    costMode === "piece"
                      ? "bg-[#8B0000] text-white"
                      : "bg-gray-100 text-gray-600"
                  }`}
                  data-testid="wiz-cost-mode-piece"
                >
                  Per piece / प्रति थान
                </button>
                <button
                  type="button"
                  onClick={() => setCostMode("kg")}
                  className={`flex-1 h-11 rounded-xl font-medium text-sm ${
                    costMode === "kg"
                      ? "bg-[#8B0000] text-white"
                      : "bg-gray-100 text-gray-600"
                  }`}
                  data-testid="wiz-cost-mode-kg"
                >
                  Per kg / प्रति के.जी.
                </button>
              </div>
            </div>

            {costMode === "kg" && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-gray-500 mb-1 block">
                      Rate / दर (रु per kg)
                    </label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={kgRate}
                      onChange={(e) => setKgRate(e.target.value)}
                      placeholder="1000"
                      className={field}
                      data-testid="wiz-kg-rate"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-500 mb-1 block">
                      Weight / तौल (kg)
                    </label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={totalKg}
                      onChange={(e) => setTotalKg(e.target.value)}
                      placeholder="5"
                      className={field}
                      data-testid="wiz-total-kg"
                    />
                  </div>
                </div>

                <div
                  className={`text-sm p-3 rounded-xl ${
                    cost > 0
                      ? "bg-green-50 text-green-700"
                      : "bg-gray-50 text-gray-500"
                  }`}
                  data-testid="wiz-per-piece"
                >
                  {cost > 0 ? (
                    <>
                      {formatQty(kgN)} kg × {formatNPR(rateN)} ={" "}
                      {formatNPR(lotTotal)} · ÷ {formatQty(qtyN)} ={" "}
                      <b>{formatNPR(Math.round(cost * 100) / 100)}</b> per piece
                      / प्रति थान · one piece ≈{" "}
                      <b>{formatQty((kgN * 1000) / qtyN)} g</b>
                    </>
                  ) : (
                    <>Fill in how many above, the rate and the weight / माथि कति वटा, दर र तौल लेख्नुहोस्</>
                  )}
                </div>
              </>
            )}

            <div className="grid grid-cols-2 gap-3">
              {costMode === "piece" && (
              <div>
                <label className="text-sm text-gray-500 mb-1 block">Cost / खरिद</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={form.cost_price}
                  onChange={(e) => handleCostChange(e.target.value)}
                  placeholder="0"
                  className={field}
                  data-testid="wiz-cost"
                />
              </div>
              )}
              <div>
                <label className="text-sm text-gray-500 mb-1 block">Tag price * / मूल्य</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={form.mrp}
                  onChange={(e) => {
                    setMrpTouched(true);
                    set("mrp", e.target.value);
                  }}
                  placeholder="0"
                  className={field}
                  data-testid="wiz-mrp"
                />
                <p className="text-xs text-gray-400 mt-1">
                  {mrpTouched ? "What staff quote" : `Auto: cost × ${markup}`}
                </p>
              </div>
            </div>

            {floor > 0 && (
              <div className="text-sm p-3 rounded-xl bg-amber-50 text-amber-800 flex items-start gap-2">
                <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  Do not sell below / कम नबेच्ने: {formatNPR(floor)}
                  {floorCode && (
                    <>
                      {" "}
                      — write{" "}
                      <span className="font-mono font-bold tracking-widest">
                        {floorCode}
                      </span>{" "}
                      on the tag
                    </>
                  )}
                </span>
              </div>
            )}
          </>
        )}

        {/* ---- extras, prefilled: one Next if nothing unusual ---- */}
        {step === "extras" && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-gray-500 mb-1 block">Sold by / बिक्री</label>
                <select
                  value={form.unit}
                  onChange={(e) => set("unit", e.target.value)}
                  className={`${field} bg-white`}
                  data-testid="wiz-unit"
                >
                  {units.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name_np} ({u.name_en})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-500 mb-1 block">Bought by / खरिद</label>
                <select
                  value={form.purchase_unit}
                  onChange={(e) => set("purchase_unit", e.target.value)}
                  className={`${field} bg-white`}
                  data-testid="wiz-purchase-unit"
                >
                  <option value="">same as sold / बिक्री जस्तै</option>
                  {units.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name_np} ({u.name_en})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-gray-500 mb-1 block">
                  Pieces per pack / प्याकमा थान
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={form.pack_size}
                  onChange={(e) => set("pack_size", e.target.value)}
                  placeholder="12"
                  className={field}
                  data-testid="wiz-pack-size"
                />
              </div>
              <div>
                <label className="text-sm text-gray-500 mb-1 block">
                  Lowest price / कम मूल्य
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={form.min_price}
                  onChange={(e) => set("min_price", e.target.value)}
                  placeholder="leave empty"
                  className={field}
                  data-testid="wiz-min-price"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-gray-500 mb-1 block">
                  Low stock alert / कम स्टक
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={form.low_stock_threshold}
                  onChange={(e) => set("low_stock_threshold", e.target.value)}
                  className={field}
                  data-testid="wiz-threshold"
                />
              </div>
              <div>
                <label className="text-sm text-gray-500 mb-1 block">Count / गणना</label>
                <div className="flex gap-1 h-14">
                  <button
                    type="button"
                    onClick={() => set("quantity_type", "exact")}
                    className={`flex-1 rounded-xl font-medium text-sm ${
                      form.quantity_type === "exact"
                        ? "bg-[#8B0000] text-white"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    Exact / सही
                  </button>
                  <button
                    type="button"
                    onClick={() => set("quantity_type", "approximate")}
                    className={`flex-1 rounded-xl font-medium text-sm ${
                      form.quantity_type === "approximate"
                        ? "bg-[#8B0000] text-white"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    ~Approx / अनुमानित
                  </button>
                </div>
              </div>
            </div>

            <div>
              <label className="text-sm text-gray-500 mb-1 block">
                <Truck className="w-4 h-4 inline mr-1" />
                Supplier / आपूर्तिकर्ता
              </label>
              <select
                value={form.supplier_id}
                onChange={(e) => set("supplier_id", e.target.value)}
                className={`${field} bg-white`}
                data-testid="wiz-supplier"
              >
                <option value="">No Supplier / छैन</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-500 mb-1 block">
                Show with its other sizes / अरू नापसँग — optional
              </label>
              <input
                type="text"
                list="wiz-groups"
                value={form.variant_group}
                onChange={(e) => set("variant_group", e.target.value)}
                placeholder={group || "worked out automatically"}
                className={field}
                data-testid="wiz-group"
              />
              <datalist id="wiz-groups">
                {existingGroups.map(([g, name]) => (
                  <option key={g} value={g}>
                    {name}
                  </option>
                ))}
              </datalist>
            </div>

            <p className="text-sm text-gray-400">
              All fine as they are? Just press Next / ठिक छ भने Next
            </p>
          </>
        )}

        {/* ---- done: the item as it will appear ---- */}
        {step === "done" && (
          <>
            <div className="bg-white rounded-2xl border-2 border-gray-200 p-5 space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-lg font-semibold">
                    {family?.en || form.name_en}
                    {form.size_value && (
                      <span className="ml-2 text-[#8B0000] font-nepali">
                        {formatSize(
                          { size_value: num(form.size_value), size_unit: form.size_unit },
                          sizeUnitsById,
                        )}
                      </span>
                    )}
                    {form.gauge && (
                      <span className="ml-2 text-sm text-gray-500 font-nepali">
                        {form.gauge} गेज
                      </span>
                    )}
                  </div>
                  <div className="text-gray-500 font-nepali">
                    {form.name_np || romanizeToNepali(form.name_en.trim()) || ""}
                  </div>
                </div>
                <PartyPopper className="w-6 h-6 text-[#8B0000]" />
              </div>

              <div className="grid grid-cols-2 gap-y-1 text-sm">
                <span className="text-gray-500">Category</span>
                <span>
                  {activeCategory?.name_en}{" "}
                  <span className="font-nepali text-gray-500">
                    {activeCategory?.name_np}
                  </span>
                </span>
                <span className="text-gray-500">Stock</span>
                <span>
                  {formatQty(num(form.quantity) || 0)} {activeUnit?.name_np} (
                  {activeUnit?.name_en})
                  {form.brand && <span className="text-gray-400"> · {form.brand}</span>}
                </span>
                <span className="text-gray-500">Cost → Tag</span>
                <span>
                  {formatNPR(cost)} → <b className="text-[#8B0000]">{formatNPR(mrp)}</b>
                </span>
                {floor > 0 && (
                  <>
                    <span className="text-gray-500">Floor</span>
                    <span>
                      {formatNPR(floor)}
                      {floorCode && (
                        <span className="ml-2 font-mono font-bold text-amber-700 tracking-widest">
                          {floorCode}
                        </span>
                      )}
                    </span>
                  </>
                )}
              </div>

              {duplicate && (
                <div className="p-2.5 rounded-lg bg-amber-50 text-amber-800 text-xs">
                  Careful — {duplicate.name_en}{" "}
                  <span className="font-nepali">
                    {formatSize(duplicate, sizeUnitsById)}
                  </span>{" "}
                  already exists. Saving makes a second row.
                </div>
              )}
            </div>
          </>
        )}
      </form>

      {/* Footer: one big obvious action per screen */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200">
        <div className="max-w-lg mx-auto space-y-2">
          {step === "done" ? (
            <>
              <button
                type="button"
                onClick={() => save(true)}
                disabled={saving}
                className="w-full h-14 bg-[#8B0000] text-white text-lg font-semibold rounded-xl
                           active:scale-95 transition-all disabled:opacity-50
                           flex items-center justify-center gap-2"
                data-testid="wiz-save-another"
              >
                <Check className="w-5 h-5" />
                Save, add another / अर्को थप्ने
              </button>
              <button
                type="button"
                onClick={() => save(false)}
                disabled={saving}
                className="w-full h-12 border-2 border-[#8B0000] text-[#8B0000] font-semibold rounded-xl
                           active:scale-95 transition-all disabled:opacity-50"
                data-testid="wiz-save-done"
              >
                Save &amp; finish / सकियो
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={next}
              className="w-full h-14 bg-[#8B0000] text-white text-lg font-semibold rounded-xl
                         active:scale-95 transition-all
                         flex items-center justify-center gap-2"
              data-testid="wiz-next"
            >
              Next / अर्को
              <ArrowRight className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
